import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, UserCheck, CalendarSearch, CalendarDays, MapPin, Trophy, Wallet, Crown, ChevronLeft, ChevronRight, X, Rocket, FileText, Star, ArrowRight } from "lucide-react";

const LEVEL_NAMES = ["Iniciante", "Intermediário", "Avançado", "Profissional"];

export default function Onboarding() {
  const { user, loadUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [pendingCall, setPendingCall] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const h = (ev) => setPendingCall(!!ev.detail);
    window.addEventListener("express-invites-pending", h);
    return () => window.removeEventListener("express-invites-pending", h);
  }, []);
  const fetchDynamic = useCallback(() => {
    Promise.all([api.get("/plans").catch(() => ({ data: [] })), api.get("/me/plan").catch(() => ({ data: null }))])
      .then(([p, u]) => { setPlans(p.data || []); setUsage(u.data); });
  }, []);

  useEffect(() => {
    if (user && user.onboarding_seen === false) { setOpen(true); fetchDynamic(); }
  }, [user, fetchDynamic]);

  useEffect(() => {
    const h = () => { setI(0); setOpen(true); fetchDynamic(); };
    window.addEventListener("open-onboarding", h);
    return () => window.removeEventListener("open-onboarding", h);
  }, [fetchDynamic]);

  const finish = async (skip) => {
    setOpen(false);
    try { await api.post("/me/onboarding-seen"); await loadUser(); } catch (e) { console.debug("onboarding-seen", e?.message); }
  };

  // Pausa o tour enquanto há um chamado ⚡ pendente ou nas telas Em cima da Hora (oferta com tempo).
  if (!open || pendingCall || pathname.startsWith("/em-cima-da-hora")) return null;

  const gratuito = plans.find((p) => p.slug === "gratuito");
  const cards = [
    { icon: Sparkles, title: "Bem-vindo ao Mí Chamou", body: (
      <p>O Mí Chamou conecta <b>músicos, contratantes e profissionais</b> para participação e contratação em eventos. Aqui você cria oportunidades, se candidata às de outros e gerencia tudo em um só lugar — sem depender de grupos de WhatsApp.</p>
    ) },
    { icon: UserCheck, title: "Complete seu perfil", body: (
      <>
        <p>Um perfil completo aumenta sua visibilidade e pode impactar positivamente seu <b>Score Mí Chamou</b>. Preencha:</p>
        <ul className="mt-2 space-y-1 text-sm text-zinc-400 list-disc pl-5">
          <li>Foto de perfil (com o rosto)</li><li>Nome profissional</li><li>Instrumentos / funções</li>
          <li>Região</li><li>Experiência e informações profissionais</li><li>Vídeos por instrumento</li>
        </ul>
      </>
    ) },
    { icon: CalendarSearch, title: "Eventos", body: (
      <>
        <p>Busque eventos, veja os <b>compatíveis</b> com seu perfil e candidate-se às vagas. O organizador seleciona os profissionais para cada vaga.</p>
        <p className="mt-2 text-sm text-zinc-500">Alguns recursos de busca e candidatura podem variar conforme o plano contratado.</p>
      </>
    ) },
    { icon: CalendarDays, title: "Agenda", body: (
      <>
        <p>Acompanhe seus <b>eventos confirmados</b>, <b>datas ocupadas</b> e evite <b>conflitos de agenda</b> automaticamente.</p>
        <p className="mt-2 text-sm text-zinc-500">Recursos como Agenda Inteligente e Modo Plantão ficam disponíveis conforme seu plano.</p>
      </>
    ) },
    { icon: MapPin, title: "Check-in e Checkout", body: (
      <p>No dia do evento, confirme sua presença com o <b>check-in</b> por proximidade e encerre sua participação com o <b>checkout</b>. Isso garante a comprovação do show e libera seus recebimentos.</p>
    ) },
    { icon: Trophy, title: "Score Mí Chamou", body: (
      <>
        <p>Seu Score cresce conforme você atua na plataforma: eventos concluídos, avaliações recebidas, check-ins e perfil completo — tudo segue as regras configuradas no sistema.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {LEVEL_NAMES.map((l) => <span key={l} className="text-[11px] px-2 py-1 rounded-sm bg-[#FF7A00]/10 text-[#FF7A00] border border-[#FF7A00]/20">{l}</span>)}
        </div>
        <p className="mt-2 text-sm text-zinc-500">No seu perfil você acompanha o Score atual, o nível e exatamente o que falta para evoluir.</p>
      </>
    ) },
    { icon: Wallet, title: "Pagamentos", body: (
      <>
        <p>Os pagamentos dos eventos acontecem dentro da plataforma via Pix. Você acompanha o <b>status</b> (pendente, pago, recebido) e o repasse cai direto na sua chave Pix.</p>
        <p className="mt-2 text-sm text-zinc-500">Podem incidir taxas da plataforma e do gateway, sempre exibidas de forma transparente. Você vê a diferença entre valores pagos, recebidos e pendentes.</p>
      </>
    ) },
    { icon: Crown, title: "Planos", body: (
      <>
        <p>Existe um <b>plano gratuito</b> e <b>planos pagos</b> com mais eventos e recursos exclusivos.</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {plans.slice(0, 4).map((p) => (
            <div key={p.slug} className="rounded-sm border border-white/10 bg-[#09090B] p-2">
              <div className="text-sm font-semibold">{p.name}</div>
              <div className="text-xs text-[#FF4D00]">{p.price > 0 ? `${money(p.price)}/mês` : "Grátis"}</div>
              <div className="text-[11px] text-zinc-500">{p.slug === "gratuito" ? "1 evento (único)" : p.event_limit < 0 ? "Eventos ilimitados" : `${p.event_limit} evento(s)/mês`}</div>
            </div>
          ))}
        </div>
        {usage && <p className="mt-2 text-[11px] text-zinc-500">Seu plano atual: <b className="text-zinc-300">{usage.plan?.name}</b>.</p>}
      </>
    ) },
    { icon: Rocket, final: true, title: "Seu perfil é o primeiro passo 🚀", body: (
      <>
        <p>Complete seu perfil para que o Mí Chamou conheça melhor o seu trabalho e possa conectar você às oportunidades certas. Um perfil completo também é uma das primeiras etapas para evolução do seu nível e Score dentro da plataforma.</p>
        <div className="mt-4 rounded-2xl border border-[#FF7A00]/20 bg-[#17120c] p-4 flex items-center gap-3">
          <FileText className="h-6 w-6 text-zinc-400" />
          <div className="flex-1"><div className="font-semibold text-sm">Perfil 10% completo</div><div className="h-1.5 mt-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full w-[10%] bg-gradient-to-r from-[#FF4D00] to-[#FF9500]" /></div></div>
        </div>
        <div className="mt-2 rounded-2xl border border-[#FF7A00]/20 bg-[#17120c] p-4 flex items-center gap-3">
          <span className="h-9 w-9 rounded-full border border-[#FF7A00]/50 grid place-items-center shrink-0"><Star className="h-4 w-4 text-[#FF7A00]" /></span>
          <div><div className="font-semibold text-sm">Score em construção</div><div className="text-xs text-zinc-400">Seu score aumenta conforme você completa o perfil e interage na plataforma.</div></div>
        </div>
      </>
    ) },
  ];

  const total = cards.length;
  const card = cards[i];
  const Icon = card.icon;
  const isLast = i === total - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="onboarding-overlay">
      <div className="w-full max-w-lg bg-[#121217] border border-white/10 rounded-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <span className="text-xs uppercase tracking-widest text-zinc-500" data-testid="onboarding-progress">{i + 1} de {total}</span>
          <button data-testid="onboarding-skip" onClick={() => { finish(true); navigate("/completar-perfil"); }} className="text-zinc-500 hover:text-white flex items-center gap-1 text-xs"><X className="h-4 w-4" />Fechar</button>
        </div>
        <div className="p-6 min-h-[300px]">
          <div className="h-12 w-12 rounded-sm bg-[#FF4D00]/15 border border-[#FF4D00]/30 flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-[#FF4D00]" /></div>
          <h2 className="font-head text-2xl font-bold tracking-tight mb-3" data-testid="onboarding-title">{card.title}</h2>
          <div className="text-zinc-300 text-sm leading-relaxed">{card.body}</div>
        </div>
        <div className="flex items-center gap-1.5 px-6 pb-3">
          {cards.map((_, idx) => <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[#FF4D00]" : "w-1.5 bg-white/15"}`} />)}
        </div>
        {card.final ? (
          <div className="px-5 py-4 border-t border-white/[0.06] space-y-2">
            <Button data-testid="onboarding-complete-profile" onClick={() => { finish(false); navigate("/completar-perfil"); }} className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF4D00] to-[#FF9500] font-bold gap-1">Completar meu perfil <ArrowRight className="h-4 w-4" /></Button>
            <Button data-testid="onboarding-later" onClick={() => finish(false)} variant="ghost" className="w-full text-zinc-400 hover:text-white underline">Agora não</Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-white/[0.06]">
            <Button data-testid="onboarding-prev" onClick={() => setI((v) => Math.max(0, v - 1))} disabled={i === 0} variant="outline" className="rounded-sm bg-transparent border-white/10 gap-1 disabled:opacity-30"><ChevronLeft className="h-4 w-4" />Voltar</Button>
            <Button data-testid="onboarding-next" onClick={() => setI((v) => Math.min(total - 1, v + 1))} className="rounded-sm bg-[#FF4D00] hover:bg-[#E64500] gap-1">Próximo<ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
